import { translate } from '../../i18n/index.js';
import type { AvailableSlot } from '@atomfit/shared';
import { formatCurrency, formatTime, TierPill } from '../design/AtomFitUI.js';

interface Props {
  slots: AvailableSlot[];
  selected: AvailableSlot | null;
  onSelect: (slot: AvailableSlot) => void;
  durationMinutes: number;
  emptyMessage?: string;
  nowMs?: number;
}

export function SlotGrid({
  slots,
  selected,
  onSelect,
  durationMinutes,
  emptyMessage = translate('components.slots.slot_grid.slot_grid.empty_message'),
  nowMs = Date.now(),
}: Props) {
  const selectedTime = selected ? String(selected.startTime) : null;
  if (slots.length === 0) {
    return (
      <div
        className="af-card"
        style={{ padding: 28, textAlign: 'center', color: 'var(--af-muted)' }}
      >
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="af-slot-grid">
      {slots.map((slot) => {
        const isSelected = selectedTime === String(slot.startTime);
        const isFull = slot.status === 'FULL';
        const isBlocked = slot.status === 'BLOCKED';
        const isPast = new Date(slot.startTime).getTime() <= nowMs;
        const isDisabled = isFull || isPast || isBlocked;
        const disabledLabel = isBlocked
          ? translate('components.slots.slot_grid.slot_grid.div_slot_grid.blocked')
          : isPast
            ? translate('components.slots.slot_grid.slot_grid.div_slot_grid.past')
            : isFull
              ? translate('components.slots.slot_grid.slot_grid.div_slot_grid.full')
              : null;
        const hasMembershipPrice =
          !isDisabled &&
          slot.publicPriceInSmallestUnit !== undefined &&
          slot.publicPriceInSmallestUnit > slot.priceInSmallestUnit;

        return (
          <button
            key={String(slot.startTime)}
            onClick={() => !isDisabled && onSelect(slot)}
            disabled={isDisabled}
            type="button"
            aria-pressed={isSelected}
            aria-label={translate(
              'components.slots.slot_grid.slot_grid.button.aria_label_content',
              {
                value1: formatTime(slot.startTime),
                value2: formatTime(slot.endTime),
                durationMinutes: durationMinutes,
                value4: isDisabled
                  ? `, ${disabledLabel}`
                  : `, ${formatCurrency(slot.priceInSmallestUnit, slot.currency)}`,
              },
            )}
            className={`af-slot-card${isSelected ? ' is-selected' : ''}${isDisabled ? ' is-disabled' : ''}`}
          >
            <span className="af-slot-card-topline">
              <span className="af-slot-card-duration">
                {durationMinutes}{' '}
                {translate('components.slots.slot_grid.slot_grid.span_slot_card_duration.copy')}
              </span>
            </span>
            <span className="af-slot-card-start">{formatTime(slot.startTime)}</span>
            <span className="af-slot-card-end">
              {translate('components.slots.slot_grid.slot_grid.span_slot_card_end.copy')}{' '}
              {formatTime(slot.endTime)}
            </span>
            <span className="af-slot-card-divider" aria-hidden="true" />
            <span className="af-slot-card-price">
              {isDisabled ? '-' : formatCurrency(slot.priceInSmallestUnit, slot.currency)}
            </span>
            {hasMembershipPrice && (
              <span className="af-slot-card-membership-price">
                <span>{formatCurrency(slot.publicPriceInSmallestUnit!, slot.currency)}</span>
                {slot.membershipPlanName
                  ? translate(
                      'components.slots.slot_grid.slot_grid.span_slot_card_membership_price.copy',
                      { value1: slot.membershipPlanName },
                    )
                  : ''}
              </span>
            )}
            {!isDisabled && slot.pricingTier !== 'STANDARD' && (
              <span className="af-slot-card-tier">
                <TierPill tier={slot.pricingTier} />
              </span>
            )}
            {disabledLabel && <span className="af-slot-card-disabled-label">{disabledLabel}</span>}
          </button>
        );
      })}
    </div>
  );
}
